import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../App";
import { 
  Send, 
  Paperclip, 
  AtSign, 
  X, 
  Smile,
  Image as ImageIcon,
  FileText
} from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

const MessageComposer = ({ onSend, onTyping, channelId, placeholder }) => {
  const { t } = useTranslation();
  const [body, setBody] = useState("");
  const [mentionQuery, setMentionQuery] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionUsers, setMentionUsers] = useState([]);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Fetch users for mentions
  const fetchMentionUsers = async (query) => {
    try {
      const response = await apiClient.get("/users");
      const filtered = response.data.filter(u => 
        u.status !== "invited" &&
        u.name?.toLowerCase().includes(query.toLowerCase())
      );
      setMentionUsers(filtered.slice(0, 5));
    } catch (e) {}
  };

  // Handle input change
  const handleChange = (e) => {
    const value = e.target.value;
    setBody(value);
    
    // Check for @ mentions
    const lastAtIndex = value.lastIndexOf("@");
    if (lastAtIndex !== -1) {
      const textAfterAt = value.slice(lastAtIndex + 1);
      const hasSpace = textAfterAt.includes(" ");
      if (!hasSpace && textAfterAt.length > 0) {
        setMentionQuery(textAfterAt);
        fetchMentionUsers(textAfterAt);
        setShowMentions(true);
      } else if (textAfterAt.length === 0) {
        fetchMentionUsers("");
        setShowMentions(true);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
    
    // Send typing indicator (debounced)
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      onTyping?.();
    }, 300);
  };

  // Insert mention
  const insertMention = (user) => {
    const lastAtIndex = body.lastIndexOf("@");
    const newBody = body.slice(0, lastAtIndex) + `@${user.name} `;
    setBody(newBody);
    setSelectedMentions(prev => [...prev, user]);
    setShowMentions(false);
    textareaRef.current?.focus();
  };

  // Remove mention
  const removeMention = (userId) => {
    setSelectedMentions(prev => prev.filter(u => u.id !== userId));
  };

  // Handle file upload
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        
        const response = await apiClient.post(
          `/chat/channels/${channelId}/attachments`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        
        setAttachments(prev => [...prev, response.data]);
      }
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Remove attachment
  const removeAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  // Send message
  const handleSend = async () => {
    if (!body.trim() && attachments.length === 0) return;
    
    setSending(true);
    try {
      // Build message body with attachment links
      let fullBody = body.trim();
      if (attachments.length > 0) {
        const attachmentText = attachments.map(a => `[${a.file_name}](${a.url})`).join("\n");
        fullBody = fullBody ? `${fullBody}\n\n${attachmentText}` : attachmentText;
      }
      
      await onSend(fullBody, selectedMentions.map(u => u.id));
      
      setBody("");
      setSelectedMentions([]);
      setAttachments([]);
    } catch (error) {
      // Error handled in parent
    } finally {
      setSending(false);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === "Escape") {
      setShowMentions(false);
    }
  };

  // Get file icon
  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith("image/")) return <ImageIcon className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  return (
    <div className="border-t border-border p-4" data-testid="message-composer">
      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {attachments.map((attachment) => (
            <div 
              key={attachment.id}
              className="flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-lg text-sm"
            >
              {getFileIcon(attachment.mime_type)}
              <span className="truncate max-w-[150px]">{attachment.file_name}</span>
              <button
                onClick={() => removeAttachment(attachment.id)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Selected Mentions */}
      {selectedMentions.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selectedMentions.map((user) => (
            <span 
              key={user.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs"
            >
              @{user.name}
              <button onClick={() => removeMention(user.id)}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Composer */}
      <div className="relative">
        {/* Mention Suggestions */}
        {showMentions && mentionUsers.length > 0 && (
          <div className="absolute bottom-full left-0 mb-2 w-64 bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-50">
            {mentionUsers.map((user) => (
              <button
                key={user.id}
                onClick={() => insertMention(user)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary text-left"
              >
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                  {user.name?.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm">{user.name}</span>
              </button>
            ))}
          </div>
        )}

        <div className="flex items-end gap-2">
          {/* Attachment Button */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 flex-shrink-0"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <Paperclip className="w-5 h-5" />
          </Button>

          {/* Text Input */}
          <div className="flex-1 relative">
            <Textarea
              ref={textareaRef}
              value={body}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || "Type a message..."}
              className="min-h-[40px] max-h-[200px] resize-none pr-10"
              rows={1}
            />
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 bottom-1 h-8 w-8"
              onClick={() => {
                setBody(body + "@");
                fetchMentionUsers("");
                setShowMentions(true);
                textareaRef.current?.focus();
              }}
            >
              <AtSign className="w-4 h-4" />
            </Button>
          </div>

          {/* Send Button */}
          <Button
            size="icon"
            className="h-9 w-9 flex-shrink-0"
            onClick={handleSend}
            disabled={(!body.trim() && attachments.length === 0) || sending}
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mt-2">
        Press Enter to send, Shift+Enter for new line. Use @ to mention someone.
      </p>
    </div>
  );
};

export default MessageComposer;
